import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, parseLocalDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface CaseActivity {
  id: number
  open_date: string | null
  investigation_type: string | null
  case_name: string | null
  referred_by: string | null
  estimated_billable: number | null
  status: string
}

interface Summary {
  active_cases: number
  total_estimated_billable: number
  average_value_per_case: number
}

interface Props {
  cases: CaseActivity[]
  start_date: string
  end_date: string
  summary: Summary
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return "-"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }
  const s = parseLocalDate(start).toLocaleDateString("en-US", opts)
  const e = parseLocalDate(end).toLocaleDateString("en-US", opts)
  return `${s} - ${e}`
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 15

const columns: HpiColumn<CaseActivity>[] = [
  {
    header: "Date",
    width: "max-w-[130px]",
    render: (row) => <div className="text-sm">{formatDate(row.open_date)}</div>,
  },
  {
    header: "Type",
    width: "max-w-[130px]",
    render: (row) => (
      <div className="text-sm">{row.investigation_type ?? "-"}</div>
    ),
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Referred By",
    render: (row) => <div className="text-sm">{row.referred_by ?? "-"}</div>,
  },
  {
    header: "Estimated",
    subHeader: "Billable",
    width: "max-w-[130px]",
    render: (row) => (
      <div className="text-sm">{formatCurrency(row.estimated_billable)}</div>
    ),
  },
  {
    header: "Status",
    width: "max-w-[80px]",
    render: (row) => <div className="text-sm">{row.status}</div>,
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Activity", href: "/reports/case_activity" },
  { title: "Case Activity", href: "/reports/case_activity" },
]

export default function CaseActivityReport({
  cases,
  start_date,
  end_date,
  summary,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(cases.length / PER_PAGE)
  const paginated = cases.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    router.get(
      "/reports/case_activity",
      {
        start_date: startDate,
        end_date: endDate,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Case Activity Report" />

      <div className="p-6">
        {/* Date Filter Bar */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        {/* Data Table */}
        <HpiDataTable<CaseActivity>
          title={`Case Activity  ${formatDateRange(start_date, end_date)}`}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          pagination={
            cases.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={cases.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Number of",
              subLabel: "Active Cases",
              value: summary.active_cases,
            },
            {
              label: "Total Estimated",
              subLabel: "Billable",
              value: formatCurrency(summary.total_estimated_billable),
            },
            {
              label: "Average Value",
              subLabel: "Per Case",
              value: formatCurrency(summary.average_value_per_case),
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
